import React, { useState } from 'react';
import { Check, Film, Trash2, ShieldCheck, AlertTriangle, FileArchive } from 'lucide-react';

/**
 * The Full Day Rough Edit is the editor's, never the client's. EDITORS, every one of them, new accounts too (Ryan,
 * 2026-10-12: "a nice very clear popup that they have to click checkbox that they have read it ... making very clear
 * if they are not working on a doc edit that they can use that xml/timeline for their highlight but must delete it
 * from the project and deliverables before uploading so the client doesnt get a free doc edit -- its purpose is only
 * to help them with the highlight").
 *
 * It says what the portal now does at upload (backend/app/rough_edit.py, QUEUE.md R38): each sentence about the check
 * is one the check really keeps. The names are the ones an editor meets: TIMELINE is the pipeline's own name for the
 * timeline, and tests/test_rough_edit_notice.py holds it to the name the upload check looks for.
 *
 * R19: nothing an editor sees may say "auto" or suggest AI. The words here are "prepared project" and "the portal".
 *
 * One box to tick, at the end of what is read, and the button stays locked until it is: a button with
 * role="checkbox", as in PreparedProjectsAnnouncement (a native checkbox cannot opt out of the global 44px tap-target
 * rule).
 */

export const TIMELINE = 'Full Day - Rough Edit';
export const AGREE = `I have read this. On highlight and social video orders I will delete “${TIMELINE}” from my project and my assets before I upload.`;

// The rule is the one thing every editor must DO: red, set here so both themes keep it red (as the Footage Pull
// update in PreparedProjectsAnnouncement). White on this red is 4.8:1.
const ALERT_RED = '#dc2626';
const ALERT_BOX = { borderColor: 'rgba(220, 38, 38, 0.55)', background: 'rgba(220, 38, 38, 0.07)' };
const TICK_FILL = '#2bb3a8';   // the site's primary button teal
const TICK_INK = '#0a0a0a';

const STEPS = [
  [Film, 'Use it as much as you like while you edit.',
    'Find your moments in it, pull from it, and build the highlight or social video with it. That is what it is for.'],
  [Trash2, 'Before you upload a draft, delete it.',
    `Remove the “${TIMELINE}” timeline from your project and save. (Premiere calls it a sequence, Final Cut Pro calls it a project.) Leave its XML file, and any export of it, out of your assets too.`],
  [AlertTriangle, 'Renaming it is not deleting it.',
    'If you built your highlight from a copy of it, give your own timeline its own name.'],
];

const CHECKS = [
  [ShieldCheck, 'On these orders the portal reads every project file and asset as it uploads. A file that still holds the timeline is not accepted, and you are told which file and where.'],
  [Check, 'To fix it: delete the timeline, save, and upload that file again. The rest of your draft stays as it is.'],
  [FileArchive, 'Zip folders as .zip. A .rar or .7z cannot be read, so it is not accepted on these orders.'],
];

export function RoughEditStaysAnnouncement({ onAcknowledge }) {
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  return (
    <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-rough-edit-stays">
      <header className="px-6 sm:px-8 pt-8 pb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border whitespace-nowrap"
                style={{ color: ALERT_RED, borderColor: ALERT_BOX.borderColor, background: ALERT_BOX.background }}>
            Please read
          </span>
          <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Highlight and social video orders</span>
        </div>
        <h2 className="font-heading text-[28px] sm:text-[34px] leading-[1.08] text-zinc-100 mb-3" style={{ textWrap: 'balance' }}>
          The Full Day Rough Edit is for you, <span className="text-ug-gold">not for the client.</span>
        </h2>
        <p className="text-[14px] text-zinc-100/80 leading-relaxed">
          On a highlight or social video order, your prepared project includes a timeline named
          <span className="text-zinc-100 font-medium"> &ldquo;{TIMELINE}&rdquo;</span>: the whole day in order, roughly cut.
          It is there for one reason, to help you make the highlight faster.
        </p>
        <div className="mt-4 rounded-xl border px-4 py-3" style={ALERT_BOX} data-testid="rough-edit-stays-rule">
          <p className="text-[13.5px] font-semibold leading-relaxed" style={{ color: ALERT_RED }}>
            Delete it from your project and your assets before you upload.
          </p>
          <p className="text-[13px] text-zinc-100/85 leading-relaxed mt-1">
            The client receives your project file and assets. A client who ordered a highlight did not order a doc edit,
            and must never be handed one for free.
          </p>
        </div>
      </header>

      <section className="px-6 sm:px-8 py-6 border-t border-ug-border-subtle" data-testid="rough-edit-stays-steps">
        <h3 className="font-heading text-[19px] leading-tight text-zinc-100 mb-3">What to do</h3>
        <ol className="grid gap-2">
          {STEPS.map(([Icon, title, detail], i) => (
            <li key={title} className="flex items-start gap-3 rounded-lg bg-ug-surface/50 border border-ug-border px-3.5 py-3">
              <span className="font-heading text-[13px] text-ug-gold tabular-nums mt-0.5">{`0${i + 1}`}</span>
              <Icon className="w-4 h-4 text-oxidized-teal shrink-0 mt-0.5" strokeWidth={2} />
              <span className="text-[13px] text-zinc-100/75 leading-relaxed">
                <span className="text-zinc-100 font-medium">{title}</span> {detail}
              </span>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-6 sm:px-8 py-6 border-t border-ug-border-subtle" data-testid="rough-edit-stays-check">
        <h3 className="font-heading text-[19px] leading-tight text-zinc-100 mb-3">The portal checks every upload</h3>
        <ul className="grid gap-2.5">
          {CHECKS.map(([Icon, text]) => (
            <li key={text} className="flex items-start gap-2.5 text-[13px] text-zinc-100/75 leading-relaxed">
              <Icon className="w-4 h-4 text-oxidized-teal shrink-0 mt-0.5" strokeWidth={2} />
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <p className="text-[12.5px] text-slate-400 leading-relaxed mt-4">
          Working on a doc edit? Nothing changes: &ldquo;Doc edit - first cut&rdquo; is your starting point and stays in your
          project. The Editor Guide has all of this under{' '}
          <a href="/editor/guide#prepared-projects" target="_blank" rel="noreferrer" data-testid="rough-edit-stays-guide-link"
             className="tap-auto text-ug-gold font-medium underline underline-offset-2">Prepared Projects</a>.
        </p>
      </section>

      {/* The box to tick, at the end of what is read and on the card's own ground (not in the footer bar: the light
          look paints that bar dark, where an empty box is hard to find, and on a phone it took a third of the card). */}
      <section className="px-6 sm:px-8 pb-6" data-testid="rough-edit-stays-confirm">
        <button type="button" role="checkbox" aria-checked={agreed} onClick={() => setAgreed(v => !v)} data-testid="rough-edit-stays-agree"
          className={`tap-auto w-full flex items-start gap-3 text-left rounded-xl px-3.5 py-3 border transition-colors ${
            agreed ? 'border-oxidized-teal/60 bg-oxidized-teal/[0.08]' : 'border-ug-border-strong bg-ug-surface/40 hover:border-ug-gold/50'}`}>
          {/* The filled box in the site's primary teal with black ink, set here so neither theme recolors it. */}
          <span className={`mt-0.5 w-[18px] h-[18px] shrink-0 rounded-md border flex items-center justify-center transition-colors ${
            agreed ? '' : 'border-ug-border-strong'}`} data-testid="rough-edit-stays-agree-box"
            style={agreed ? { background: TICK_FILL, borderColor: TICK_FILL } : undefined}>
            {agreed && <Check className="w-3 h-3" style={{ color: TICK_INK }} strokeWidth={3.5} />}
          </span>
          <span className={`text-[12.5px] leading-relaxed ${agreed ? 'text-zinc-100' : 'text-zinc-100/80'}`}>{AGREE}</span>
        </button>
      </section>

      <footer className="sticky bottom-0 px-6 sm:px-8 py-5 border-t border-ug-border-subtle bg-ug-raised/95 backdrop-blur">
        <button type="button" disabled={!agreed || saving} data-testid="whats-new-next"
          onClick={async () => { setSaving(true); try { await onAcknowledge?.(); } finally { setSaving(false); } }}
          className="tap-auto w-full py-3 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? 'Saving…' : agreed ? 'Got it' : 'Tick the box to continue'}
        </button>
      </footer>
    </div>
  );
}

export default RoughEditStaysAnnouncement;
